export const MAX_AVAILABILITY_GROUPS_PER_REQUEST = 200;
export const MAX_AVAILABILITY_IDS_PER_REQUEST = 5000;

export interface TradeItemAvailabilityGroup {
  ids: string[];
}

export interface TradeItemAvailabilityBatchRequest {
  groups: TradeItemAvailabilityGroup[];
}

/**
 * Canonicalize API-provided item groups for both the request body and the
 * React Query key. Group order and ID order must not create separate cache
 * entries for the same visible inventory.
 */
export function canonicalizeAvailabilityGroups(
  items: readonly { ids: readonly string[] }[],
  itemLimit?: number
): TradeItemAvailabilityGroup[] {
  const visibleItems =
    itemLimit === undefined ? items : items.slice(0, itemLimit);
  const byKey = new Map<string, TradeItemAvailabilityGroup>();

  for (const item of visibleItems) {
    if (item.ids.length === 0) continue;
    const ids = [...new Set(item.ids)].sort();
    const key = JSON.stringify(ids);
    if (!byKey.has(key)) byKey.set(key, { ids });
  }

  return [...byKey.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([, group]) => group);
}

/**
 * Build stable availability groups for infinite-query pages.
 *
 * The limit applies to the flattened visible rows, matching the compact-view
 * behavior of `canonicalizeAvailabilityGroups`. A group is assigned to its
 * first page only, so overlapping page boundaries do not issue duplicate
 * exact-ID reads. Appending a page therefore leaves every existing page's
 * group set unchanged and creates work only for the appended rows.
 */
export function canonicalizeAvailabilityGroupsByPage(
  pages: readonly (readonly { ids: readonly string[] }[])[],
  itemLimit?: number
): TradeItemAvailabilityGroup[][] {
  const seen = new Set<string>();
  let remaining = itemLimit ?? Number.POSITIVE_INFINITY;

  return pages.map((items) => {
    if (remaining <= 0) return [];

    const visibleItems = Number.isFinite(remaining)
      ? items.slice(0, remaining)
      : items;
    remaining -= visibleItems.length;

    return canonicalizeAvailabilityGroups(visibleItems).filter((group) => {
      const key = JSON.stringify(group.ids);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
}

/** Split canonical groups without ever exceeding either API request limit. */
export function chunkAvailabilityGroups(
  groups: readonly TradeItemAvailabilityGroup[]
): TradeItemAvailabilityGroup[][] {
  const chunks: TradeItemAvailabilityGroup[][] = [];
  let current: TradeItemAvailabilityGroup[] = [];
  let currentIdCount = 0;

  for (const group of groups) {
    if (group.ids.length > MAX_AVAILABILITY_IDS_PER_REQUEST) {
      // A grouped browse row should never approach this bound. If corrupt or
      // synthetic data does, omit it from the compact overlay and let the
      // mutation endpoint remain the final availability authority.
      continue;
    }

    const exceedsGroupLimit =
      current.length >= MAX_AVAILABILITY_GROUPS_PER_REQUEST;
    const exceedsIdLimit =
      currentIdCount + group.ids.length > MAX_AVAILABILITY_IDS_PER_REQUEST;
    if (current.length > 0 && (exceedsGroupLimit || exceedsIdLimit)) {
      chunks.push(current);
      current = [];
      currentIdCount = 0;
    }

    current.push(group);
    currentIdCount += group.ids.length;
  }

  if (current.length > 0) chunks.push(current);
  return chunks;
}
